import React, { Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { notification, DatePicker, Alert, Button, Spin, Table, Select } from 'antd';
import { PageContainer } from '@ant-design/pro-layout';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AuthContext } from 'context/AuthContext';
import reportService from 'services/report.service';
import branchService from 'services/branch.service';
import { formatDateForClient, formatDateForServer, formatMoney } from 'helpers/utils';
import download from 'js-file-download';
import { storage } from 'helpers';
import 'antd/dist/reset.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const resultStyle = {
  loading: {
    padding: '30px 50px',
    margin: '20px 0',
    textAlign: 'center',
  },
};

class CurrentAccount extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      reports: [],
      loading: false,
      startDate: formatDateForClient(dayjs().subtract(365, 'days')),
      endDate: formatDateForClient(),
      currentMerch: context?.selectedMerch,
      selectedBranch: '0',
      downloading: false,
      columns: [
        {
          title: <div className="table-title-calibri-bold-12">Tarih</div>,
          dataIndex: 'Tarih',
          key: 'Tarih',
          align: 'center',
          width: 80,
          render: (text, row) => (
            <span className="table-title-calibri-bold-12">
              {row.Date ? dayjs(row.Date).format('DD.MM.YYYY') : ''}
            </span>
          ),
        },
        {
          title: <div className="table-title-calibri-bold-12">Belge Türü / Numarası</div>,
          dataIndex: 'Type',
          key: 'Type',
          align: 'center',
          width: 80,
          render: (text, row) => {
            return (
              <div style={{ textAlign: 'center' }}>
                <span className="table-title-calibri-bold-12">{row.OperationName || ''} </span>
                <br />
                <span className="table-content-calibri-light-12"> {row.BelgeNo || ''} </span>
              </div>
            );
          },
        },
        {
          title: <div className="table-title-calibri-bold-12">Takip No</div>,
          dataIndex: 'TakipNo',
          key: 'TakipNo',
          align: 'center',
          width: 80,
          render: (text, row) => (
            <div className="text-align-center">
              <span className="table-title-calibri-bold-12">{row.TakipNo}</span>
            </div>
          ),
        },
        {
          title: <div className="table-title-calibri-bold-12">Şube No</div>,
          dataIndex: 'BranchId',
          key: 'BranchId',
          align: 'center',
          width: 80,
          render: (text, row) => (
            <div className="text-align-center">
              <span className="table-content-calibri-bold-11">{row.BranchId}</span>
            </div>
          ),
        },
        {
          title: <div className="table-title-calibri-bold-12">Borç</div>,
          dataIndex: 'Borc',
          key: 'Borc',
          align: 'center',
          width: 80,
          render: (text, row) => (
            <div className="text-align-center">
              <span className="table-content-calibri-bold-11">{formatMoney(row.Borc)}</span>
            </div>
          ),
        },
        {
          title: <div className="table-title-calibri-bold-12">Alacak</div>,
          dataIndex: 'Alacak',
          key: 'Alacak',
          align: 'center',
          width: 80,
          render: (text, row) => (
            <div className="text-align-center">
              <span className="table-content-calibri-bold-11">{formatMoney(row.Alacak)}</span>
            </div>
          ),
        },
        {
          title: <div className="table-title-calibri-bold-12">Bakiye</div>,
          dataIndex: 'Bakiye',
          key: 'Bakiye',
          align: 'center',
          width: 80,
          render: (text, row) => (
            <div className="text-align-center">
              <span className="table-title-calibri-bold-12">
                {' '}
                {`${formatMoney(row?.Bakiye)} ${row?.Letter}`}{' '}
              </span>
            </div>
          ),
        },
      ],
    };
  }

  componentDidMount() {
    this.getReports();
    this.getCurrentMerchBranches();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState?.currentMerch?.number !== this.context?.selectedMerch?.number &&
      this.state?.currentMerch?.number === prevState.currentMerch?.number
    ) {
      this.getReports();
      this.getCurrentMerchBranches();
    }
  }

  getReports = () => {
    this.setState({ loading: true, currentMerch: this.context?.selectedMerch }, async () => {
      try {
        const { currentMerch, endDate, startDate, selectedBranch } = this.state;
        if (currentMerch?.number) {
          const { payload } = await reportService.getCurrentAccountReports(
            currentMerch?.number,
            formatDateForServer(startDate),
            formatDateForServer(endDate),
            selectedBranch,
          );
          this.setState({ loading: false, reports: payload });
        }
      } catch (error) {
        this.setState({ loading: false, reports: [] });
        notification.error({
          message: 'İşlem Başarısız',
          description: error,
        });
      }
    });
  };

  onDateChange = (dates) => {
    if (!dates || dates.length !== 2) return;
    const startDate = formatDateForClient(dates[0]);
    const endDate = formatDateForClient(dates[1]);
    this.setState({ startDate, endDate }, () => {
      storage.set({ startDate, endDate }, 'date-filters');
      this.getReports();
    });
  };

  getCurrentMerchBranches = async () => {
    try {
      this.setState({ loading: true, currentMerch: this.context?.selectedMerch });
      const number = this.context?.selectedMerch?.number;
      if (number) {
        const { payload } = await branchService.getBranchesByMerch(number);
        this.setState({ loading: false, branches: payload });
      }
    } catch (error) {
      this.setState({ loading: false });
      notification.error({
        message: 'İşlem Başarısız',
        description: error,
      });
    }
  };

  onBranchChange = (selectedBranch) => {
    this.setState({ selectedBranch }, () => {
      storage.set({ selectedBranch }, 'branch-filters');
      this.getReports();
    });
  };

  renderBranchOptions = () => {
    const { branches } = this.state;
    return branches?.map((branch) => (
      <Option key={branch.BranchId} value={branch.BranchId}>
        {branch.BranchTitle}
      </Option>
    ));
  };

  downloadExcel = async () => {
    try {
      this.setState({ downloading: true });
      const { currentMerch, startDate, endDate, selectedBranch } = this.state;
      const data = await reportService.getCurrentAccountReportsExcel(
        currentMerch?.number,
        formatDateForServer(startDate),
        formatDateForServer(endDate),
        selectedBranch,
      );
      download(data, `CariHesap-${currentMerch?.number}-${dayjs().format('DD-MM-YYYY')}.xls`);
      this.setState({ downloading: false });
    } catch (error) {
      this.setState({ loading: false, downloading: false });
      notification.error({
        message: 'İşlem Başarısız',
        description: error,
      });
    }
  };

  render() {
    const { reports, columns, downloading, loading, startDate, endDate } = this.state;

    return (
      <PageContainer
        title="Cari Hesap Raporu"
        extra={[
          <RangePicker
            allowClear={false}
            defaultValue={[dayjs(startDate, 'DD.MM.YYYY'), dayjs(endDate, 'DD.MM.YYYY')]}
            key="eod"
            onChange={this.onDateChange}
            format="DD.MM.YYYY"
          />,
          <Select
            key="branch"
            loading={loading}
            defaultValue={null}
            onChange={this.onBranchChange}
            style={{ width: 200 }}
          >
            <Option value={null} key="ALL">
              Tüm Şubeler
            </Option>
            {this.renderBranchOptions()}
          </Select>,
          <Button
            loading={downloading}
            disabled={loading || downloading}
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              this.downloadExcel();
            }}
          >
            İndir
          </Button>,
        ]}
      >
        {loading && (
          <div style={resultStyle.loading}>
            <Spin />
          </div>
        )}
        {!loading && (
          <div>
            {reports && reports.length > 0 ? (
              <Alert
                style={{ marginBottom: 15 }}
                message="Seçilen tarih aralığındaki cari hesap rapor sonuçları aşağıdaki gibidir."
                type="info"
                showIcon
              />
            ) : (
              <Alert
                style={{ marginBottom: 15 }}
                message="Seçilen tarih aralığında cari hesap rapor sonucu bulunamadı."
                type="warning"
                showIcon
              />
            )}

            <Table
              loading={loading}
              dataSource={reports}
              columns={columns}
              rowKey={(record, index) => index}
            />
          </div>
        )}
      </PageContainer>
    );
  }
}

CurrentAccount.contextType = AuthContext;

const CurrentAccountWrapper = (props) => {
  const navigate = useNavigate();
  const params = useParams();
  return <CurrentAccount {...props} navigate={navigate} params={params} />;
};

export default CurrentAccountWrapper;
